import { Component } from '@theme/component';
import {
  isLightBackground,
  isMobileBreakpoint,
  panelCartInnerHasItems,
  syncCartPanelFromSource,
} from '@theme/utilities';
import { CartAddAnimationCompleteEvent, CartSectionsMorphedEvent } from '@theme/events';
import { loadDeferredScript, loadDeferredStylesheet } from '@theme/deferred-scripts';
import { acquireScrollLock, releaseScrollLock } from '@theme/scroll-lock';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/** @type {Record<string, string>} */
const PANEL_DEFERRED_SCRIPTS = (() => {
  try {
    const el = document.getElementById('ea-panel-deferred-scripts');
    return el?.textContent ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
})();

/** @type {Record<string, string>} */
const DEFERRED_STYLES = (() => {
  try {
    const el = document.getElementById('ea-deferred-styles');
    return el?.textContent ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
})();

/** Maps panel pane id → deferred script key in #ea-panel-deferred-scripts JSON */
const PANEL_SCRIPT_KEYS = {
  'panel-search': 'panelSearch',
  'panel-ai': 'aiPanel',
  'panel-account': 'accountPanel',
};

/** Custom element to await after deferred panel scripts load */
const PANEL_CUSTOM_ELEMENTS = {
  'panel-search': 'panel-search-component',
};

/** Desktop breakpoint — hover panels (≥ 1030 px). */
const desktopMQ = window.matchMedia('(min-width: 1030px)');
/** Mobile breakpoint — tap-to-open from bottom nav (≤ 1029 px). */
const mobileMQ = window.matchMedia('(max-width: 1029px)');

/** Bottom-nav action → pane ID. */
const MOBILE_PANEL_IDS = {
  search: 'panel-search',
  cart: 'panel-cart',
  ai: 'panel-ai',
  account: 'panel-account',
  menu: 'panel-menu',
};

const DRAWER_DIALOG_ID = 'ea-right-panel';

/** How long the cart panel stays open after an add-to-cart auto-open, before it auto-closes. */
const CART_AUTO_CLOSE_DELAY = 3500;

/**
 * Single persistent right-panel drawer. One <dialog> stays open; [data-panel-pane]
 * regions swap visibility — no close/reopen cycle when switching triggers.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @extends {Component<Refs>}
 */
class RightPanelComponent extends Component {
  requiredRefs = ['dialog'];

  static #instance = null;
  static #initialized = false;
  static #clickListenerAdded = false;

  /** @type {string|null} */
  static #activePanelId = null;

  /** @type {Element|null} */
  static #trigger = null;

  static #justClosed = false;
  static #justClosedTimer = 0;
  static #hoverTimer = 0;
  static #hoverListenersWired = false;
  static #overlayDismissWired = false;

  static #fabricDropdownDisabled = false;
  static #headerEl = null;
  static #scrollLocked = false;
  static #openGeneration = 0;
  static #autoCloseTimer = 0;
  /** @type {AbortController|null} */
  static #autoCloseController = null;
  static #headerHydrationPromise = null;
  static #cartPanelContentFresh = false;
  static #mobileActiveAction = null;
  static #mobileMenuSetup = false;

  static #disableFabricDropdown() {
    if (RightPanelComponent.#fabricDropdownDisabled) return;
    RightPanelComponent.#fabricDropdownDisabled = true;

    const noop = () => {};
    const disableAll = () => {
      document.querySelectorAll('header-menu').forEach((hm) => {
        hm.activate = noop;
      });
    };

    if (customElements.get('header-menu')) {
      disableAll();
    } else {
      customElements.whenDefined('header-menu').then(disableAll);
    }
  }

  static #suppressIconDefaults() {
    window.addEventListener('click', RightPanelComponent.#onPanelTriggerClick, true);
  }

  static #onPanelTriggerClick = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-panel-id]');
    if (!trigger) return;

    const panelId = trigger.getAttribute('data-panel-id');
    if (!panelId) return;

    if (mobileMQ.matches) {
      if (!trigger.hasAttribute('data-panel-suppress-default')) return;

      if (panelId === 'panel-cart') {
        event.preventDefault();
        event.stopImmediatePropagation();
        RightPanelComponent.openMobile('cart');
      }
      return;
    }

    const drawerOpen = RightPanelComponent.#isDrawerOpen();
    const suppressDefault = trigger.hasAttribute('data-panel-suppress-default');

    if (!suppressDefault && !drawerOpen) return;

    if (suppressDefault || drawerOpen) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (drawerOpen && RightPanelComponent.#activePanelId === panelId) return;

    RightPanelComponent.#openPanel(panelId, trigger);
  };

  static #getInstance() {
    return RightPanelComponent.#instance ?? document.querySelector('right-panel-component');
  }

  static #getHeader() {
    if (!RightPanelComponent.#headerEl) {
      RightPanelComponent.#headerEl = document.querySelector('.header[transparent]');
    }
    return RightPanelComponent.#headerEl;
  }

  static #getDialog() {
    return document.getElementById(DRAWER_DIALOG_ID);
  }

  static #isDrawerOpen() {
    return RightPanelComponent.#getDialog()?.open ?? false;
  }

  static #getOpenPanelId() {
    if (!RightPanelComponent.#isDrawerOpen()) return null;
    return RightPanelComponent.#activePanelId;
  }

  static #setMobileActiveState(action) {
    RightPanelComponent.#mobileActiveAction = action;

    document.querySelectorAll('[data-ea-bottom-nav]').forEach((el) => {
      el.removeAttribute('data-ea-bottom-nav-active');
      el.setAttribute('aria-expanded', 'false');

      if (el instanceof HTMLButtonElement) {
        const openLabel = el.dataset.eaBottomNavLabelOpen;
        if (openLabel) el.setAttribute('aria-label', openLabel);
      }
    });

    if (action) {
      const btn = document.querySelector(`[data-ea-bottom-nav="${action}"]`);
      btn?.setAttribute('data-ea-bottom-nav-active', '');
      btn?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ea-mobile-panel-open');

      if (btn instanceof HTMLButtonElement) {
        const closeLabel = btn.dataset.eaBottomNavLabelClose;
        if (closeLabel) btn.setAttribute('aria-label', closeLabel);
      }
    } else {
      document.body.classList.remove('ea-mobile-panel-open');
    }
  }

  static openMobile(action) {
    if (!mobileMQ.matches) return;

    const panelId = MOBILE_PANEL_IDS[action];
    if (!panelId) return;

    RightPanelComponent.#openPanel(panelId, null, action);
  }

  static toggleMobile(action) {
    if (!mobileMQ.matches) return;

    const panelId = MOBILE_PANEL_IDS[action];
    if (!panelId) return;

    if (RightPanelComponent.#isDrawerOpen() && RightPanelComponent.#activePanelId === panelId) {
      RightPanelComponent.#closeAll();
      return;
    }

    RightPanelComponent.#openPanel(panelId, null, action);
  }

  static closeAll() {
    RightPanelComponent.#closeAll();
  }

  static isMobileOpen(action) {
    if (action) {
      const panelId = MOBILE_PANEL_IDS[action];
      return RightPanelComponent.#isDrawerOpen() && RightPanelComponent.#activePanelId === panelId;
    }
    return RightPanelComponent.#isDrawerOpen();
  }

  static #resolveDismissCursorMode() {
    const header = document.querySelector('.header');
    if (header?.hasAttribute('transparent')) return 'dark';

    const surface =
      document.querySelector('#MainContent .collection-wrapper') ??
      document.querySelector('#MainContent .section-background') ??
      document.querySelector('#MainContent');

    if (surface instanceof HTMLElement && isLightBackground(getComputedStyle(surface).backgroundColor)) {
      return 'light';
    }

    return 'light';
  }

  static #setDismissCursor() {
    document.body.dataset.dismissCursor = RightPanelComponent.#resolveDismissCursorMode();
  }

  static #clearDismissCursor() {
    delete document.body.dataset.dismissCursor;
  }

  static #resetFabricState() {
    document.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('header-menu[aria-expanded="true"]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
  }

  static #lockScroll() {
    if (RightPanelComponent.#scrollLocked) return;
    RightPanelComponent.#scrollLocked = true;
    acquireScrollLock();
  }

  static #unlockScroll() {
    if (!RightPanelComponent.#scrollLocked) return;
    RightPanelComponent.#scrollLocked = false;
    releaseScrollLock();
  }

  /** @param {number} generation */
  static #scheduleAutoClose(generation) {
    RightPanelComponent.#cancelAutoClose();

    const dialog = RightPanelComponent.#getDialog();
    if (!dialog) return;

    const controller = new AbortController();
    RightPanelComponent.#autoCloseController = controller;
    const cancel = () => RightPanelComponent.#cancelAutoClose();
    for (const type of ['pointerenter', 'pointermove', 'wheel', 'touchstart', 'focusin']) {
      dialog.addEventListener(type, cancel, { signal: controller.signal });
    }

    RightPanelComponent.#autoCloseTimer = setTimeout(() => {
      if (generation !== RightPanelComponent.#openGeneration) return;
      RightPanelComponent.#closeAll();
    }, CART_AUTO_CLOSE_DELAY);
  }

  static #cancelAutoClose() {
    clearTimeout(RightPanelComponent.#autoCloseTimer);
    RightPanelComponent.#autoCloseController?.abort();
    RightPanelComponent.#autoCloseController = null;
  }

  static #closeAll() {
    const wasOpen = RightPanelComponent.#isDrawerOpen() || document.body.classList.contains('mega-menu-open');
    if (!wasOpen) return;

    RightPanelComponent.#cancelAutoClose();
    RightPanelComponent.#openGeneration++;
    RightPanelComponent.#cancelPanelHoverOpen();

    const dialog = RightPanelComponent.#getDialog();
    dialog?.querySelectorAll('[data-nav-active]').forEach((el) => el.removeAttribute('data-nav-active'));

    const closingMenu = RightPanelComponent.#activePanelId === 'panel-menu';

    RightPanelComponent.#hideAllPanes();
    RightPanelComponent.#activePanelId = null;
    RightPanelComponent.#trigger?.removeAttribute('data-panel-active');
    RightPanelComponent.#trigger = null;

    if (closingMenu) {
      RightPanelComponent.#resetMobileMenu();
    }

    dialog?.close();
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.removeAttribute('open');
    }

    RightPanelComponent.#getInstance()?.removeAttribute('data-active-panel');
    RightPanelComponent.#getHeader()?.removeAttribute('data-panel-open');
    document.querySelectorAll('[data-panel-active]').forEach((el) => el.removeAttribute('data-panel-active'));
    RightPanelComponent.#resetFabricState();
    document.body.classList.remove('mega-menu-open');
    RightPanelComponent.#clearDismissCursor();
    RightPanelComponent.#unlockScroll();
    RightPanelComponent.#setMobileActiveState(null);

    document.removeEventListener('click', RightPanelComponent.#onDocumentClick, true);
    RightPanelComponent.#clickListenerAdded = false;

    clearTimeout(RightPanelComponent.#justClosedTimer);
    RightPanelComponent.#justClosed = true;
    RightPanelComponent.#justClosedTimer = setTimeout(() => {
      RightPanelComponent.#justClosed = false;
    }, 350);

    document.dispatchEvent(new CustomEvent('ea:panel-closed'));
  }

  static #onOverlayDismiss = (/** @type {Event} */ event) => {
    if (!desktopMQ.matches) return;
    if (!RightPanelComponent.#isDrawerOpen()) return;
    event.preventDefault();
    RightPanelComponent.#closeAll();
  };

  static #wireOverlayDismiss() {
    if (RightPanelComponent.#overlayDismissWired) return;
    const overlay = document.querySelector('.right-panel__overlay');
    if (!overlay) return;
    RightPanelComponent.#overlayDismissWired = true;
    overlay.addEventListener('click', RightPanelComponent.#onOverlayDismiss);
  }

  static #onDocumentClick = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.right-panel__dialog')) return;

    if (mobileMQ.matches) {
      if (event.target.closest('.ea-mobile-bottom-nav')) return;
      if (event.target.closest('.header-logo')) return;
      RightPanelComponent.#closeAll();
      return;
    }

    if (event.target.closest('header-component')) {
      if (event.target.closest('a, button, [data-panel-id], [role="button"]')) return;
      RightPanelComponent.#closeAll();
      return;
    }

    if (event.target.closest('.right-panel__overlay')) return;

    if (event.target.closest('a[href], button, input[type="submit"], label[for]')) {
      event.preventDefault();
    }

    RightPanelComponent.#closeAll();
  };

  static #hideAllPanes() {
    RightPanelComponent.#getDialog()?.querySelectorAll('[data-panel-pane]').forEach((pane) => {
      pane.hidden = true;
      pane.setAttribute('aria-hidden', 'true');
    });
  }

  static #finishOpen(panelId, mobileAction) {
    const dialog = RightPanelComponent.#getDialog();
    if (!dialog) return;

    RightPanelComponent.#showPane(panelId);
    dialog.show();
    document.body.classList.add('mega-menu-open');

    if (desktopMQ.matches) {
      RightPanelComponent.#setDismissCursor();
    }

    RightPanelComponent.#lockScroll();

    if (!RightPanelComponent.#clickListenerAdded) {
      document.addEventListener('click', RightPanelComponent.#onDocumentClick, true);
      RightPanelComponent.#clickListenerAdded = true;
    }

    RightPanelComponent.#syncDrawerChrome(panelId, mobileAction);
  }

  static #openPanel(panelId, trigger, mobileAction = null, options = {}) {
    const dialog = RightPanelComponent.#getDialog();
    if (!dialog) return;

    if (desktopMQ.matches && RightPanelComponent.#justClosed && !options.fromHover) return;

    RightPanelComponent.#cancelAutoClose();
    RightPanelComponent.#openGeneration++;
    const generation = RightPanelComponent.#openGeneration;
    const drawerWasOpen = dialog.open;
    const wasAlreadyOnThisPanel = drawerWasOpen && RightPanelComponent.#activePanelId === panelId;

    RightPanelComponent.#trigger = trigger;

    if (drawerWasOpen) {
      RightPanelComponent.#showPane(panelId);
      RightPanelComponent.#syncDrawerChrome(panelId, mobileAction);
      if (panelId === 'panel-cart') {
        RightPanelComponent.#syncCartPanelHydratingState();
      }
      RightPanelComponent.#runPostOpenDeferredWork(panelId, generation);
      // Don't auto-close a panel the user already had open before this add-to-cart.
      if (options.autoClose && !wasAlreadyOnThisPanel) RightPanelComponent.#scheduleAutoClose(generation);
      return;
    }

    // Open shell immediately for every panel — nav and icon triggers share the same
    // hover path. Deferred scripts/styles/hydration must not gate dialog.show().
    RightPanelComponent.#finishOpen(panelId, mobileAction);
    if (panelId === 'panel-cart') {
      RightPanelComponent.#syncCartPanelHydratingState();
    }
    RightPanelComponent.#runPostOpenDeferredWork(panelId, generation);
    if (options.autoClose) RightPanelComponent.#scheduleAutoClose(generation);
  }

  /**
   * @param {string} panelId
   * @param {number} generation
   */
  static #runPostOpenDeferredWork(panelId, generation) {
    void RightPanelComponent.#ensureDeferredPanelAssets(panelId)
      .then(() => {
        if (generation !== RightPanelComponent.#openGeneration) return;
        if (!RightPanelComponent.#isDrawerOpen() || RightPanelComponent.#activePanelId !== panelId) return;

        if (panelId === 'panel-search') {
          void RightPanelComponent.#syncPanelSearchInput(panelId, generation);
        }
      })
      .catch(() => {});
  }

  /**
   * @param {string} panelId
   * @param {number} generation
   */
  static #syncPanelSearchInput(panelId, generation) {
    if (panelId !== 'panel-search') return;

    void customElements.whenDefined('panel-search-component').then(() => {
      if (generation !== RightPanelComponent.#openGeneration) return;
      if (!RightPanelComponent.#isDrawerOpen() || RightPanelComponent.#activePanelId !== panelId) return;

      const searchComponent = RightPanelComponent.#getDialog()?.querySelector('#panel-search panel-search-component');
      if (searchComponent && 'syncActiveQuery' in searchComponent) {
        /** @type {{ syncActiveQuery: () => void }} */ (searchComponent).syncActiveQuery();
      }
    });
  }

  /**
   * @param {string} panelId
   */
  static #showPane(panelId) {
    const dialog = RightPanelComponent.#getDialog();
    if (!dialog) return;

    dialog.querySelectorAll('[data-panel-pane]').forEach((pane) => {
      const isActive = pane.id === panelId;
      pane.hidden = !isActive;
      pane.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    RightPanelComponent.#activePanelId = panelId;

    const pane = document.getElementById(panelId);
    const title = pane instanceof HTMLElement ? pane.dataset.panelTitle : '';
    if (title) dialog.setAttribute('aria-label', title);

    RightPanelComponent.#mountLazyNavContent(panelId);
    RightPanelComponent.#setupNavGroups(panelId);

    dialog.querySelector('.right-panel__inner')?.scrollTo({ top: 0, behavior: 'instant' });

    if (panelId === 'panel-menu' && mobileMQ.matches) {
      dialog.querySelector('#panel-menu .right-panel__menu-flat')?.scrollTo({ top: 0, behavior: 'instant' });
      RightPanelComponent.#resetMobileMenu();
      requestAnimationFrame(() => {
        const { activeElement } = document;
        if (activeElement instanceof HTMLElement && dialog.contains(activeElement)) {
          activeElement.blur();
        }
      });
    }
  }

  /**
   * @param {string} panelId
   * @param {string|null} mobileAction
   */
  static #syncDrawerChrome(panelId, mobileAction) {
    RightPanelComponent.#getInstance()?.setAttribute('data-active-panel', panelId);
    RightPanelComponent.#getHeader()?.setAttribute('data-panel-open', panelId);

    document.querySelectorAll('[data-panel-active]').forEach((el) => el.removeAttribute('data-panel-active'));
    RightPanelComponent.#trigger?.setAttribute('data-panel-active', '');

    if (mobileMQ.matches && mobileAction) {
      RightPanelComponent.#setMobileActiveState(mobileAction);
    }
  }

  /**
   * @param {string} panelId
   */
  static #mountLazyNavContent(panelId) {
    const pane = document.getElementById(panelId);
    const slot = pane?.querySelector('[data-nav-template-id]:not([data-nav-mounted])');
    if (!slot) return;

    const templateId = slot.getAttribute('data-nav-template-id');
    if (!templateId) return;

    const template = document.getElementById(templateId);
    if (!(template instanceof HTMLTemplateElement)) return;

    slot.replaceChildren(template.content.cloneNode(true));
    slot.setAttribute('data-nav-mounted', 'true');
  }

  /**
   * @param {string} panelId
   * @returns {Promise<void>}
   */
  static #ensureCartPanelHydrated(panelId) {
    if (panelId !== 'panel-cart') return Promise.resolve();

    // Already confirmed fresh this page-load, either by a mutation earlier in
    // this session (add-to-cart, in-panel quantity/remove — see
    // CartSectionsMorphedEvent handling in #registerCartAutoOpen) or by an
    // earlier call to this function. Nothing to fetch.
    if (RightPanelComponent.#cartPanelContentFresh) {
      RightPanelComponent.#setCartPanelHydrating(false);
      return Promise.resolve();
    }

    const sectionId = PANEL_DEFERRED_SCRIPTS.headerSectionId;
    if (!sectionId) return Promise.resolve();

    if (panelCartInnerHasItems()) {
      RightPanelComponent.#cartPanelContentFresh = true;
      RightPanelComponent.#setCartPanelHydrating(false);
      return Promise.resolve();
    }

    if (!RightPanelComponent.#cartLikelyHasItems()) {
      RightPanelComponent.#cartPanelContentFresh = true;
      RightPanelComponent.#setCartPanelHydrating(false);
      return Promise.resolve();
    }

    RightPanelComponent.#setCartPanelHydrating(true);

    const normalizedId = normalizeSectionId(String(sectionId));

    if (!RightPanelComponent.#headerHydrationPromise) {
      RightPanelComponent.#headerHydrationPromise = (async () => {
        // Always fetch fresh markup for the hidden source node rather than
        // trusting whatever this page's own server render happened to embed
        // — that embedded snapshot is only as fresh as this page's request,
        // and Shopify's cart write isn't guaranteed visible to the very next
        // read (cart eventual consistency), which is what made the panel show
        // stale/empty content right after navigating away from an add.
        //
        // Deliberately NOT sectionRenderer.renderSection(..., mode: 'hydration')
        // here — that morphs every data-hydration-key node in the header
        // section, including the live pane's own right-panel-cart-inner node,
        // which the Liquid template always server-renders as bare skeleton
        // regardless of cart state. That would clobber the pane back to
        // skeleton and require a second pass to patch it straight back up.
        const url = new URL(window.location.href);
        url.searchParams.delete('page');

        const html = await sectionRenderer.getSectionHTML(normalizedId, false, url);
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const freshSource = fragment.querySelector('[data-hydration-key="right-panel-cart-source"]');
        const liveSource = document.querySelector(
          '[data-ea-cart-panel-hydration-source] [data-hydration-key="right-panel-cart-source"]'
        );

        if (freshSource && liveSource) {
          morph(liveSource, freshSource, { childrenOnly: true });
          syncCartPanelFromSource();
        }

        RightPanelComponent.#cartPanelContentFresh = true;
      })().finally(() => {
        RightPanelComponent.#setCartPanelHydrating(false);
        RightPanelComponent.#headerHydrationPromise = null;
      });
    }

    return RightPanelComponent.#headerHydrationPromise;
  }

  /**
   * Whether the cart panel inner still needs hydration and likely has line items.
   * @returns {boolean}
   */
  static #cartLikelyHasItems() {
    if (panelCartInnerHasItems()) return false;

    const ssrCount = Number(PANEL_DEFERRED_SCRIPTS.cartItemCount);
    if (Number.isFinite(ssrCount) && ssrCount > 0) return true;

    const bubbleCountEl = document.querySelector('.cart-bubble__text-count:not(.hidden)');
    const bubbleCount = Number(bubbleCountEl?.textContent);
    if (Number.isFinite(bubbleCount) && bubbleCount > 0) return true;

    return Boolean(document.querySelector('cart-icon.header-actions__cart-icon--has-cart'));
  }

  /**
   * @param {boolean} isHydrating
   */
  static #setCartPanelHydrating(isHydrating) {
    const pane = document.getElementById('panel-cart');
    if (!pane) return;

    if (isHydrating) {
      pane.setAttribute('data-hydrating', 'true');
      pane.setAttribute('aria-busy', 'true');
      return;
    }

    pane.removeAttribute('data-hydrating');
    pane.removeAttribute('aria-busy');
  }

  /**
   * Shows cart skeleton immediately on open when line items are still pending.
   */
  static #syncCartPanelHydratingState() {
    if (panelCartInnerHasItems() || !RightPanelComponent.#cartLikelyHasItems()) {
      RightPanelComponent.#setCartPanelHydrating(false);
      return;
    }

    RightPanelComponent.#setCartPanelHydrating(true);
  }

  /**
   * @param {string} panelId
   * @returns {Promise<void>}
   */
  static #ensureDeferredPanelAssets(panelId) {
    /** @type {Promise<void>[]} */
    const tasks = [RightPanelComponent.#ensureDeferredPanelScript(panelId)];

    if (panelId === 'panel-search' && DEFERRED_STYLES.predictiveSearch) {
      tasks.push(loadDeferredStylesheet(DEFERRED_STYLES.predictiveSearch));
    }

    if (panelId === 'panel-cart') {
      tasks.push(RightPanelComponent.#ensureCartPanelHydrated(panelId));
    }

    return Promise.all(tasks).then(() => undefined);
  }

  /**
   * @param {string} panelId
   * @returns {Promise<void>}
   */
  static #ensureDeferredPanelScript(panelId) {
    const scriptKey = PANEL_SCRIPT_KEYS[panelId];
    if (!scriptKey) return Promise.resolve();

    const scriptUrl = PANEL_DEFERRED_SCRIPTS[scriptKey];
    if (!scriptUrl) return Promise.resolve();

    const customElementName = PANEL_CUSTOM_ELEMENTS[panelId];

    return loadDeferredScript(scriptUrl).then(() => {
      if (customElementName && !customElements.get(customElementName)) {
        return customElements.whenDefined(customElementName);
      }
    });
  }

  /**
   * @param {string} panelId
   */
  static #setupNavGroups(panelId) {
    const pane = document.getElementById(panelId);
    const nav = pane?.querySelector('.right-panel__nav');
    if (!nav || nav.dataset.navSetup) return;
    nav.dataset.navSetup = 'true';

    nav.addEventListener('mouseover', (event) => {
      if (nav.matches('[data-mobile-menu]') && mobileMQ.matches) return;
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(
        '.right-panel__group-label, .right-panel__standalone-link, .right-panel__group-link',
      );
      if (!trigger) return;

      const item = trigger.closest('.right-panel__group') ?? trigger;
      if (item.hasAttribute('data-nav-active')) return;

      nav.querySelectorAll('[data-nav-active]').forEach((el) => el.removeAttribute('data-nav-active'));
      item.setAttribute('data-nav-active', '');
    });

    RightPanelComponent.#setupMobileMenu(nav);
  }

  static #setupMobileMenu(/** @type {Element} */ nav) {
    if (!nav.matches('[data-mobile-menu]') || RightPanelComponent.#mobileMenuSetup) return;
    RightPanelComponent.#mobileMenuSetup = true;

    nav.addEventListener('click', (event) => {
      if (!mobileMQ.matches) return;
      if (!(event.target instanceof Element)) return;

      const toggle = event.target.closest('.right-panel__group-toggle');
      if (toggle instanceof HTMLButtonElement) {
        event.preventDefault();
        event.stopPropagation();
        const group = toggle.closest('.right-panel__group');
        if (group) RightPanelComponent.#toggleMobileMenuGroup(nav, group);
        return;
      }

      const groupLabel = event.target.closest('#panel-menu .right-panel__group-label');
      if (groupLabel instanceof HTMLButtonElement) {
        event.preventDefault();
        const group = groupLabel.closest('.right-panel__group');
        if (group) RightPanelComponent.#toggleMobileMenuGroup(nav, group);
      }
    });
  }

  /**
   * @param {Element} nav
   * @param {Element} group
   */
  static #toggleMobileMenuGroup(nav, group) {
    const isOpen = group.hasAttribute('data-nav-active');
    const toggle = group.querySelector('.right-panel__group-toggle');

    nav.querySelectorAll('[data-nav-active]').forEach((el) => {
      el.removeAttribute('data-nav-active');
      const otherToggle = el.querySelector('.right-panel__group-toggle');
      if (otherToggle instanceof HTMLButtonElement) {
        otherToggle.setAttribute('aria-expanded', 'false');
        otherToggle.setAttribute('aria-label', `Expand ${otherToggle.closest('.right-panel__group')?.querySelector('.right-panel__group-label')?.textContent?.trim() ?? ''}`);
      }
    });

    if (isOpen) return;

    group.setAttribute('data-nav-active', '');
    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', `Collapse ${group.querySelector('.right-panel__group-label')?.textContent?.trim() ?? ''}`);
    }
  }

  static #resetMobileMenu() {
    const nav = document.querySelector('#panel-menu [data-mobile-menu]');
    if (!nav) return;

    nav.querySelectorAll('[data-nav-active]').forEach((el) => el.removeAttribute('data-nav-active'));
    nav.querySelectorAll('.right-panel__group-toggle').forEach((toggle) => {
      if (toggle instanceof HTMLButtonElement) {
        toggle.setAttribute('aria-expanded', 'false');
        const label = toggle.closest('.right-panel__group')?.querySelector('.right-panel__group-label')?.textContent?.trim() ?? '';
        toggle.setAttribute('aria-label', `Expand ${label}`);
      }
    });

    nav.closest('.right-panel__inner')?.scrollTo({ top: 0, behavior: 'instant' });
    nav.querySelector('.right-panel__menu-flat')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  static #cancelPanelHoverOpen() {
    clearTimeout(RightPanelComponent.#hoverTimer);
    RightPanelComponent.#hoverTimer = 0;
  }

  /**
   * @param {Element} trigger
   */
  static #schedulePanelHoverOpen(trigger) {
    if (!desktopMQ.matches) return;

    const panelId = trigger.getAttribute('data-panel-id');
    if (!panelId) return;

    if (RightPanelComponent.#isDrawerOpen() && RightPanelComponent.#activePanelId === panelId) return;

    RightPanelComponent.#cancelPanelHoverOpen();
    const delay = RightPanelComponent.#isDrawerOpen() ? 0 : 120;
    RightPanelComponent.#hoverTimer = window.setTimeout(() => {
      RightPanelComponent.#hoverTimer = 0;
      RightPanelComponent.#openPanel(panelId, trigger, null, { fromHover: true });
    }, delay);
  }

  static #onTriggerPointerEnter = (/** @type {PointerEvent} */ event) => {
    if (!desktopMQ.matches || !(event.currentTarget instanceof Element)) return;
    RightPanelComponent.#schedulePanelHoverOpen(event.currentTarget);
  };

  static #onTriggerPointerLeave = (/** @type {PointerEvent} */ event) => {
    if (!desktopMQ.matches) return;
    const related = event.relatedTarget;
    if (related instanceof Element) {
      if (related.closest('[data-panel-id], right-panel-component, .right-panel__dialog, header-component')) {
        return;
      }
    }
    RightPanelComponent.#cancelPanelHoverOpen();
  };

  static #wireHoverTrigger(/** @type {Element} */ trigger) {
    if (trigger.dataset.eaHoverWired) return;
    trigger.dataset.eaHoverWired = 'true';
    trigger.addEventListener('pointerenter', RightPanelComponent.#onTriggerPointerEnter);
    trigger.addEventListener('pointerleave', RightPanelComponent.#onTriggerPointerLeave);
  }

  static #wireHoverTriggers(/** @type {ParentNode} */ root = document) {
    if (root instanceof Element && root.hasAttribute('data-panel-id')) {
      RightPanelComponent.#wireHoverTrigger(root);
    }
    if (root instanceof Element || root instanceof Document || root instanceof DocumentFragment) {
      root.querySelectorAll('[data-panel-id]').forEach((trigger) => {
        RightPanelComponent.#wireHoverTrigger(trigger);
      });
    }
  }

  static #wireHoverListeners() {
    if (RightPanelComponent.#hoverListenersWired) return;
    RightPanelComponent.#hoverListenersWired = true;

    RightPanelComponent.#wireHoverTriggers(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            RightPanelComponent.#wireHoverTriggers(node);
          }
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('ea:panel-closed', () => {
      requestAnimationFrame(() => {
        const pos = window.__eaLastPointer;
        if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return;
        const target = document.elementFromPoint(pos.x, pos.y);
        const trigger = target instanceof Element ? target.closest('[data-panel-id]') : null;
        if (trigger instanceof Element) {
          RightPanelComponent.#schedulePanelHoverOpen(trigger);
        }
      });
    });
  }

  static #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape') RightPanelComponent.#closeAll();
  };

  static #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) RightPanelComponent.#closeAll();
  };

  static #onMobileBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) RightPanelComponent.#closeAll();
  };

  static #registerGlobalListeners() {
    if (RightPanelComponent.#initialized) return;
    RightPanelComponent.#initialized = true;

    RightPanelComponent.#disableFabricDropdown();
    RightPanelComponent.#suppressIconDefaults();
    RightPanelComponent.#wireHoverListeners();
    RightPanelComponent.#wireOverlayDismiss();
    RightPanelComponent.#registerCartAutoOpen();

    document.addEventListener('keydown', RightPanelComponent.#onKeyDown);
    desktopMQ.addEventListener('change', RightPanelComponent.#onBreakpointChange);
    mobileMQ.addEventListener('change', RightPanelComponent.#onMobileBreakpointChange);
  }

  static #registerCartAutoOpen() {
    // Panel open is sequenced off the button's animation finishing (see
    // product-form.js), not off the add succeeding — so the cart update itself
    // (morph + bubble sync) stays eager and isn't delayed waiting for the
    // animation.
    document.addEventListener(CartAddAnimationCompleteEvent.eventName, RightPanelComponent.#handleCartAdd);
    // Dispatched right after a mutation's response has been morphed into the
    // live pane (product-form.js add-to-cart, component-cart-items.js
    // quantity/remove) — the only reliable "panel content is now correct"
    // signal, so it's what marks content fresh for the rest of this
    // page-load (see #ensureCartPanelHydrated).
    document.addEventListener(CartSectionsMorphedEvent.eventName, RightPanelComponent.#markCartPanelContentFresh);
  }

  static #markCartPanelContentFresh = () => {
    RightPanelComponent.#cartPanelContentFresh = true;
    if (panelCartInnerHasItems()) {
      RightPanelComponent.#setCartPanelHydrating(false);
    }
  };

  /**
   * Opens cart panel on desktop add when Theme Editor auto-open is enabled.
   * Fires once the add-to-cart button's success animation has finished.
   * @param {Event} event
   */
  static #handleCartAdd = (event) => {
    if (!(event instanceof CartAddAnimationCompleteEvent)) return;
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (isMobileBreakpoint()) return;
    if (!PANEL_DEFERRED_SCRIPTS.autoOpenCartPanel) return;

    const trigger = document.querySelector('[data-panel-id="panel-cart"]');
    RightPanelComponent.#openPanel('panel-cart', trigger, null, { autoClose: true });
  };

  static scheduleHoverFromBootstrap(/** @type {Element} */ trigger) {
    RightPanelComponent.#schedulePanelHoverOpen(trigger);
  }

  connectedCallback() {
    super.connectedCallback();
    RightPanelComponent.#instance = this;
    RightPanelComponent.#registerGlobalListeners();
    this.addEventListener('click', this.#onPanelClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onPanelClick);
    if (RightPanelComponent.#instance === this) {
      RightPanelComponent.#instance = null;
    }
  }

  #onPanelClick = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-panel-close]')) return;
    event.preventDefault();
    RightPanelComponent.#closeAll();
  };
}

if (!customElements.get('right-panel-component')) {
  customElements.define('right-panel-component', RightPanelComponent);
}

window.eaRightPanel = {
  open: (/** @type {string} */ action) => RightPanelComponent.openMobile(action),
  toggle: (/** @type {string} */ action) => RightPanelComponent.toggleMobile(action),
  close: () => RightPanelComponent.closeAll(),
  isOpen: (/** @type {string} */ [action]) => RightPanelComponent.isMobileOpen(action),
  __scheduleHover: (/** @type {Element} */ trigger) => RightPanelComponent.scheduleHoverFromBootstrap(trigger),
};
